import React, { Component } from 'react';
import { getBlockDef } from './BlockRenderer';

// ── Error boundary to prevent rendering issues from crashing the app ──
class LayoutErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.warn('LayoutEditor error caught:', error.message);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '16px', color: '#856404', background: '#fff3cd', borderRadius: 4, margin: '8px 0', fontSize: 13 }}>
          <strong>Layout editor encountered an error.</strong>
          <p style={{ margin: '6px 0 0' }}>Try removing some blocks from a page, then save.</p>
          <button
            style={{ marginTop: 8, padding: '4px 12px', cursor: 'pointer', border: '1px solid #856404', borderRadius: 4, background: '#fff' }}
            onClick={() => this.setState({ hasError: false })}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Miniature block rectangle for page thumbnail ──
const MiniBlock = ({ block, customBlocks, canvasW, canvasH, gridCols, gridRows }) => {
  const def = getBlockDef(block.id, customBlocks);
  const left = (block.x / gridCols) * 100;
  const top = (block.y / gridRows) * 100;
  const width = (block.w / gridCols) * 100;
  const height = (block.h / gridRows) * 100;

  const typeClass = def?.type === 'text' ? 'mini-text' : def?.type === 'data' ? 'mini-data' : 'mini-special';

  return (
    <div
      className={`layout-mini-block ${typeClass}`}
      style={{
        position: 'absolute',
        left: `${left}%`,
        top: `${top}%`,
        width: `${width}%`,
        height: `${height}%`,
      }}
      title={def?.label || block.id}
    >
      <span className="mini-block-label">{def?.label || block.id}</span>
    </div>
  );
};

// ── Page Thumbnail — clickable miniature A4 preview ──
const PageThumbnail = ({ page, pageIndex, customBlocks, onClick, onRemovePage }) => {
  const gridCols = 12;
  // Calculate max row used to set a sensible gridRows
  const maxRow = page.blocks.reduce((max, b) => Math.max(max, (b.y || 0) + (b.h || 6)), 0);
  const gridRows = Math.max(53, maxRow);

  return (
    <div className="layout-page-column">
      <div className="layout-page-header">
        <span>Page {pageIndex + 1}</span>
        {onRemovePage && (
          <button
            className="layout-remove-page-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (page.blocks.length > 0) {
                if (!window.confirm(`Remove page ${pageIndex + 1} and its ${page.blocks.length} block(s)?`)) return;
              }
              onRemovePage(pageIndex);
            }}
            title="Remove this page"
          >
            &times;
          </button>
        )}
      </div>
      <div
        className="layout-page-thumbnail"
        onClick={() => onClick(pageIndex)}
        title={`Click to edit Page ${pageIndex + 1} layout`}
      >
        {page.blocks.length === 0 ? (
          <div className="layout-empty-page">Click to add blocks</div>
        ) : (
          page.blocks.map(block => (
            <MiniBlock
              key={block.id}
              block={block}
              customBlocks={customBlocks}
              gridCols={gridCols}
              gridRows={gridRows}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ── Layout Editor — page thumbnail grid ──
const LayoutEditor = ({ layout, onLayoutChange, onAddBlock, onRemoveBlock, onAddPage, onRemovePage, customBlocks }) => {
  const scrollToPage = (pageIndex) => {
    const pageEl = document.querySelector(`.qr-page.page-${pageIndex + 1}`);
    if (pageEl) pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <LayoutErrorBoundary>
      <div className="layout-editor-wrapper">
        <p className="layout-editor-hint">
          Click a page thumbnail to scroll to it. Use the overlays on the preview to drag, resize, and remove blocks.
        </p>
        <div className="layout-editor">
          {layout.map((page, pageIndex) => (
            <PageThumbnail
              key={pageIndex}
              page={page}
              pageIndex={pageIndex}
              customBlocks={customBlocks}
              onClick={scrollToPage}
              onRemovePage={onRemovePage}
            />
          ))}
          {onAddPage && (
            <div className="layout-add-page-column">
              <button
                className="layout-add-page-btn"
                onClick={onAddPage}
                title="Add a new page"
              >
                + Add Page
              </button>
            </div>
          )}
        </div>
      </div>
    </LayoutErrorBoundary>
  );
};

export default LayoutEditor;
